import "core-js/modules/es.array.includes.js";
import { computed, ref, unref, watch, h, reactive, defineComponent, createVNode as _createVNode } from 'vue';
import { VxeModal } from 'vxe-pc-ui';
import { XButton, XIcon } from '@xui/components/base';
import siteLogo from '@/assets/images/logo.png';
import _ from 'lodash';
const defaultIcon = () => {
  return h('img', {
    src: siteLogo,
    alt: 'logo',
    className: 'site-logo'
  });
};
const HeadIcon = props => {
  if (!props.icon) return defaultIcon();
  return () => _createVNode(XIcon, {
    "icon": props.icon
  }, null);
};
export default defineComponent({
  name: 'XModal',
  components: {
    VxeModal,
    XButton,
    HeadIcon
  },
  props: {
    id: {
      type: [String, Number],
      default: ''
    },
    title: {
      type: String,
      default: ''
    },
    icon: {
      type: [String, Object, Function]
    },
    type: {
      type: String,
      validator: value => ['info', 'success', 'warning', 'error', 'question', 'loading'].includes(value)
    },
    mode: {
      type: String,
      default: 'modal',
      validator: value => ['alert', 'confirm', 'message', 'notification', 'modal'].includes(value)
    },
    okText: {
      type: String,
      default: '确定'
    },
    cancelText: {
      type: String,
      default: '取消'
    },
    keyboard: {
      type: Boolean,
      default: true
    },
    showOkButton: {
      type: Boolean,
      default: true
    },
    showCancelButton: {
      type: Boolean,
      default: true
    },
    open: {
      type: Boolean,
      default: false
    },
    destroyOnClose: {
      type: Boolean,
      default: false
    },
    content: {
      type: String,
      default: ''
    },
    params: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    },
    className: {
      type: String,
      default: ''
    },
    iconStatus: {
      type: String,
      default: ''
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    lockView: {
      type: Boolean,
      default: true
    },
    lockScroll: {
      type: Boolean,
      default: true
    },
    mask: {
      type: Boolean,
      default: true
    },
    maskClosable: {
      type: Boolean,
      default: true
    },
    showFooter: {
      type: Boolean,
      default: true
    },
    okClosable: {
      type: Boolean,
      default: false
    },
    cancelClosable: {
      type: Boolean,
      default: true
    },
    showMaximize: {
      type: Boolean,
      default: false
    },
    showMinimize: {
      type: Boolean,
      default: false
    },
    showZoom: {
      type: Boolean,
      default: false
    },
    zoomConfig: {
      type: Object,
      default: () => ({
        minimizeLayout: 'vertical',
        minimizeMaxSize: 10,
        minimizeVerticalOffset: {
          top: -24,
          left: 0
        },
        minimizeHorizontalOffset: {
          top: 0,
          left: 32
        },
        minimizeOffsetMethod: ({
          top,
          left
        }) => ({
          top,
          left
        })
      })
    },
    showClose: {
      type: Boolean,
      default: true
    },
    resize: {
      type: Boolean,
      default: false
    },
    marginSize: {
      type: Number,
      default: 0
    },
    duration: {
      type: Number,
      default: 3000
    },
    width: {
      type: [Number, String],
      default: 400
    },
    height: {
      type: [Number, String]
    },
    minWidth: {
      type: [Number, String]
    },
    minHeight: {
      type: [Number, String]
    },
    top: {
      type: [Number, String],
      default: 15
    },
    position: {
      default: () => ({
        left: '',
        top: ''
      })
    },
    zIndex: {
      type: Number
    },
    titleOverflow: {
      type: Boolean,
      default: true
    },
    transfer: {
      type: Boolean,
      default: false
    },
    fullscreen: {
      type: Boolean,
      default: false
    },
    draggable: {
      type: Boolean,
      default: true
    },
    dblclickZoom: {
      type: Boolean,
      default: true
    },
    remember: {
      type: Boolean,
      default: false
    },
    storage: {
      type: Boolean,
      default: false
    },
    beforeHide: {
      type: Function
    },
    okIcon: {
      type: [String, Object],
      default: 'vxe-icon-check'
    },
    okLoading: {
      type: Boolean,
      default: false
    },
    okDisabled: {
      type: Boolean,
      default: false
    },
    cancelIcon: {
      type: [String, Object],
      default: 'vxe-icon-close'
    },
    cancelLoading: {
      type: Boolean,
      default: false
    },
    cancelDisabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['show', 'hide', 'close', 'ok', 'cancel', 'close', 'zoom', 'resize', 'move', 'update:open'],
  setup(props, {
    emit,
    expose
  }) {
    const modalRef = ref();
    const watchProps = computed(() => {
      return {
        title: props.title,
        status: props.type,
        type: props.mode,
        escClosable: props.keyboard,
        loading: props.loading,
        className: props.className,
        iconStatus: props.iconStatus,
        content: props.content,
        destroyOnClose: props.destroyOnClose,
        showCancelButton: props.showCancelButton,
        cancelButtonText: props.cancelText,
        showConfirmButton: props.showOkButton,
        confirmButtonText: props.okText,
        showHeader: props.showHeader,
        showFooter: props.showFooter,
        lockView: props.lockView,
        lockScroll: props.lockScroll,
        mask: props.mask,
        maskClosable: props.maskClosable,
        confirmClosable: props.okClosable,
        cancelClosable: props.cancelClosable,
        showMaximize: props.showMaximize,
        showMinimize: props.showMinimize,
        showZoom: props.showZoom,
        zoomConfig: props.zoomConfig,
        showClose: props.showClose,
        resize: props.resize,
        marginSize: props.marginSize || 0,
        duration: props.duration || 3000,
        width: props.width || 400,
        height: props.height,
        minWidth: props.minWidth,
        minHeight: props.minHeight,
        top: props.top || 15,
        position: props.position,
        zIndex: props.zIndex,
        showTitleOverflow: props.titleOverflow,
        transfer: props.transfer,
        fullscreen: props.fullscreen,
        draggable: props.draggable,
        dblclickZoom: props.dblclickZoom,
        remember: props.remember,
        storage: props.storage,
        beforeHideMethod: props.beforeHide
      };
    });
    const modalProps = reactive({
      title: props.title,
      status: props.type,
      type: props.mode,
      escClosable: props.keyboard,
      loading: props.loading,
      className: props.className,
      iconStatus: props.iconStatus,
      content: props.content,
      destroyOnClose: props.destroyOnClose,
      showCancelButton: props.showCancelButton,
      cancelButtonText: props.cancelText,
      showConfirmButton: props.showOkButton,
      confirmButtonText: props.okText,
      showHeader: props.showHeader,
      showFooter: props.showFooter,
      lockView: props.lockView,
      lockScroll: props.lockScroll,
      mask: props.mask,
      maskClosable: props.maskClosable,
      confirmClosable: props.okClosable,
      cancelClosable: props.cancelClosable,
      showMaximize: props.showMaximize,
      showMinimize: props.showMinimize,
      showZoom: props.showZoom,
      zoomConfig: {
        minimizeLayout: props.zoomConfig?.minimizeLayout || 'vertical',
        // horizontal | vertical
        minimizeMaxSize: props.zoomConfig?.minimizeMaxSize || 10,
        // 最大尺寸
        minimizeVerticalOffset: props.zoomConfig?.minimizeVerticalOffset || {
          top: -24,
          left: 0
        },
        // 垂直方向偏移
        minimizeHorizontalOffset: props.zoomConfig?.minimizeHorizontalOffset || {
          top: 0,
          left: 32
        },
        // 水平方向偏移
        minimizeOffsetMethod: props.zoomConfig?.minimizeOffsetMethod || (({
          top,
          left
        }) => ({
          top,
          left
        })) // 偏移方法
      },
      showClose: props.showClose,
      resize: props.resize,
      marginSize: props.marginSize || 0,
      duration: props.duration || 3000,
      width: props.width || 400,
      height: props.height,
      minWidth: props.minWidth,
      minHeight: props.minHeight,
      top: props.top || 15,
      position: {
        top: props.position?.top,
        left: props.position?.left
      },
      zIndex: props.zIndex,
      showTitleOverflow: props.titleOverflow,
      transfer: props.transfer,
      fullscreen: props.fullscreen,
      draggable: props.draggable,
      dblclickZoom: props.dblclickZoom,
      remember: props.remember,
      storage: props.storage,
      beforeHideMethod: props.beforeHide
    });
    watch(() => watchProps, val => {
      const newValues = _.omitBy(unref(val), _.isUndefined || _.isNull);
      const diffFields = _.pickBy(newValues, (value, key) => !_.isEqual(value, modalProps[key]));
      if (Object.keys(diffFields).length > 0) {
        Object.assign(modalProps, diffFields);
      }
    }, {
      deep: true,
      immediate: true
    });
    const buttonProps = computed(() => {
      return {
        ok: {
          title: props.okText,
          visible: props.showOkButton,
          disabled: props.okDisabled,
          loading: props.okLoading,
          type: 'primary',
          icon: props.okIcon || 'vxe-icon-check'
        },
        cancel: {
          title: props.cancelText,
          type: 'danger',
          visible: props.showCancelButton,
          disabled: props.cancelDisabled,
          loading: props.cancelLoading,
          icon: props.cancelIcon || 'vxe-icon-close'
        }
      };
    });
    const buttonEvents = {
      ok: {
        click({
          $event
        }) {
          emit('ok', $event);
          if (props.okClosable) {
            modalMethods.close();
          }
        }
      },
      cancel: {
        click({
          $event
        }) {
          emit('cancel', $event);
          if (props.cancelClosable) {
            modalMethods.close();
          }
        }
      }
    };
    const visible = ref(unref(props.open));
    watch(() => props.open, val => {
      visible.value = unref(val);
    }, {
      immediate: true
    });

    // const defaultIcon = h('img', {
    //     src: siteLogo,
    //     className: 'site-logo',
    // });

    // const HeadIcon = computed(() => {
    //     if (!props.icon) return () => defaultIcon;
    //     if (typeof props.icon === 'string') {
    //         if (props.icon.startsWith('vxe-icon-')) {
    //             return () => <VxeIcon name={props.icon} />;
    //         } else if (props.icon.startsWith('fa-')) {
    //             return () => <i class={`fa-1x ${props.icon}`} />;
    //         } else {
    //             return () => <i class={props.icon} />;
    //         }
    //     } else if ((props.icon instanceof HTMLElement) || isVNode(props.icon)) {
    //         return () => props.icon;
    //     } else if (typeof props.icon === 'object' && props.icon.render === 'function') {
    //         return () => h(props.icon);
    //     }
    //     return () => defaultIcon;
    // });

    const dialogEvents = {
      show: ({
        type
      }) => {
        emit('show', type);
        emit('update:open', true);
      },
      hide: ({
        type
      }) => {
        emit('hide', type);
        emit('close', type);
        emit('update:open', false);
      },
      confirm: ({
        type,
        $event
      }) => {
        emit('ok', {
          type,
          $event
        });
      },
      cancel: ({
        type,
        $event
      }) => {
        emit('cancel', {
          type,
          $event
        });
      },
      close: ({
        type,
        $event
      }) => {
        emit('close', {
          type,
          $event
        });
      },
      zoom: ({
        type,
        $event
      }) => {
        emit('zoom', {
          type,
          $event
        });
      },
      resize: ({
        type,
        $event
      }) => {
        emit('resize', {
          type,
          $event
        });
      },
      move: ({
        type,
        $event
      }) => {
        emit('move', {
          type,
          $event
        });
      }
    };
    const modalMethods = {
      open: () => {
        !!modalRef.value && modalRef.value.open();
      },
      close: () => {
        !!modalRef.value && modalRef.value.close();
      },
      getBox: () => {
        if (!modalRef.value) {
          return modalRef.value.getBox();
        }
        return null;
      },
      getPosition: () => {
        if (!modalRef.value) {
          return modalRef.value.getPosition();
        }
        return {
          top: -1,
          left: -1
        };
      },
      setPosition: (top, left) => {
        !!modalRef.value && modalRef.value.setPosition(top, left);
      },
      isMaximized: () => {
        if (!modalRef.value) {
          return modalRef.value.isMaximized();
        }
        return false;
      },
      zoom: () => {
        !!modalRef.value && modalRef.value.zoom();
      },
      minimize: () => {
        !!modalRef.value && modalRef.value.minimize();
      },
      maximize: () => {
        !!modalRef.value && modalRef.value.maximize();
      },
      revert: () => {
        !!modalRef.value && modalRef.value.revert();
      }
    };
    expose(modalMethods);
    return {
      modalProps,
      buttonProps,
      buttonEvents,
      visible,
      modalRef,
      // HeadIcon,
      dialogEvents,
      modalMethods
    };
  }
});